import Modal from "@via/components/modal.tsx";
import SelectComponent from "@via/components/select/select.tsx";
import { NOTIFICATION } from "@via/context/notifications-context.tsx";
import { useNotifications } from "@via/hooks/useNotifications.tsx";
import { useChangesDetect } from "@via/hooks/useChangesDetect";
import { useCreatePipelineRevision } from "@via/pages/projects/pipelines/pipelines.api.tsx";
import {
  extractSha,
  filterEmptyFolders,
  packDraftForSave,
  packScriptConfig,
} from "@via/pages/projects/pipelines/workflow/utils/helpers.tsx";
import { isPipelineExternal } from "@via/pages/projects/runs/utils/helpers.tsx";
import { SemanticRevisionTypes, ModalSize } from "@via/utils/constants.tsx";
import { checkIsSemanticVersion } from "@via/utils/helpers.tsx";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

const RevisePipelineModal = ({ open, setOpen, draft, scripts }) => {
  const navigate = useNavigate();
  const [payload, setPayload] = useState<any>(null);
  const [revisionType, setRevisionType] = useState(SemanticRevisionTypes[0]);
  const [sha, setSha] = useState<string | null>(null);
  const [visibleRevComment, setVisibleRevComment] = useState("");
  const { setNotification } = useNotifications();
  const {
    mutateAsync: createOrRevisePipeline,
    isPending: isPipelineRevisionLoading,
  } = useCreatePipelineRevision();
  const { resetChangeCount } = useChangesDetect();

  useEffect(() => {
    if (!draft || !scripts) return;

    const filteredEmptyFolders = filterEmptyFolders(scripts);
    const config = packScriptConfig(filteredEmptyFolders);
    const formattedDraft = packDraftForSave(draft, config);
    const fullComment = (formattedDraft?.revComment as string) || "";

    if (isPipelineExternal(draft.pipelineType)) {
      const extractedSha = extractSha(fullComment);
      setSha(extractedSha);
      setVisibleRevComment(
        fullComment.replace(/\s*\([\da-f]{7,40}\)/i, "").trim()
      );
    } else {
      setVisibleRevComment(fullComment);
    }
    setPayload(formattedDraft);
  }, [draft, scripts, revisionType]);

  // Awaiting the revision is what closes the dialog: resolving hands the close to
  // Modal, and a rejection leaves it open with the mutation's own error on screen.
  // The navigation moved here from a success effect for the same reason.
  //
  // resetChangeCount now runs only once the revision has actually been created. It
  // used to fire the moment the request was sent, so a failed revision cleared the
  // unsaved changes marker on an editor whose changes were still unsaved.
  const handleConfirmModal = async (): Promise<void> => {
    const finalComment =
      isPipelineExternal(draft.pipelineType) && sha
        ? `(${sha}) ${visibleRevComment.trim()}`
        : visibleRevComment.trim();

    const submission = {
      ...payload,
      revisionType: revisionType.value,
      revComment: finalComment,
    };
    const { pipelineRevUUId, pipelineUUId, ...bodyPayload } = submission;
    const revised = await createOrRevisePipeline(bodyPayload);
    resetChangeCount();
    navigate(`/pipelines/${revised.id}`);
  };

  const handleRevisionTypeChange = selected => {
    const isValidVersion = checkIsSemanticVersion(payload?.version, true);
    if (!isValidVersion) {
      setNotification({
        message: "Invalid Version Detected",
        details: "Acceptable Versions Must Be Semantic (x.x.x)",
        variant: NOTIFICATION.ERROR,
      });
      return;
    }
    setRevisionType(selected);
  };

  // This is extra insurance that modal data is destroyed
  const handleCancel = () => {
    setOpen(false);
    setRevisionType(SemanticRevisionTypes[0]);
    setPayload(null);
  };

  return (
    <Modal
      open={open}
      setOpen={setOpen}
      title="Create Pipeline Revision"
      confirmText="Create Revision"
      size={ModalSize.Large}
      onCancel={handleCancel}
      isConfirmLoading={isPipelineRevisionLoading}
      onConfirm={handleConfirmModal}>
      <form>
        <div className="mt-0">
          <span className="text-sm text-gray-600">
            The current version of this pipeline is{" "}
            <span className="font-medium">{payload?.version}</span>. The new
            version will be calculated automatically based on the revision type.
          </span>
          <div className="my-6 w-full">
            <label className="mt-4 mb-2 block text-sm font-medium text-gray-900">
              Revision Type
            </label>
            <SelectComponent
              enableClear={false}
              name="revisionTypes"
              onChange={handleRevisionTypeChange}
              options={SemanticRevisionTypes}
              value={revisionType}
            />
          </div>
          <div className="my-6 w-full">
            <label className="mt-4 mb-2 block text-sm font-medium text-gray-900">
              Comment
            </label>
            <textarea
              rows={4}
              className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
              placeholder={"Enter a Revision Comment"}
              value={visibleRevComment}
              onChange={e => setVisibleRevComment(e.target.value)}
            />
            {isPipelineExternal(draft.pipelineType) &&
              extractSha(draft?.revComment) && (
                <label className="mt-4 block text-sm font-medium text-gray-900">
                  SHA:{" "}
                  <span className="font-normal">
                    {extractSha(draft?.revComment)}
                  </span>
                </label>
              )}
          </div>
        </div>
      </form>
    </Modal>
  );
};

export default RevisePipelineModal;
